// ARM `machine_evidence` shape-oracle fixture — copied verbatim from the
// illustrative oracle in API_CONTRACTS.md ~2942-2971. The NUMBERS are ASPIRATIONAL
// (the DYNAMIC question card is registered but NOT emitted this milestone — GAP-10);
// this fixture exists ONLY to drive the SP-4 viewer + the SP-6 fixture harness so we
// can visually verify the renderer before the flip. It exercises: hero rate with
// denominator (4.61% · 479 / 10,386), a threshold-band supporting figure, a
// `cold_armed` supporting figure ("armed, not yet captured", NOT 0), an `on_target`
// verdict, STALE freshness, a `links_to.machine_card_tab = "arm"` deep-link, and the
// dormant frozen/drift/recapture chrome ({is_frozen:false}).
import type { MachineEvidenceArtifactData } from "../../../chatBot/models/machineEvidenceModels";

export const ARM_EVIDENCE_ORACLE: MachineEvidenceArtifactData = {
  schema_version: 1,
  mode: "snapshot",
  subsystem: "arm",
  verdict: {
    status: "on_target",
    headline: "Reject rate 4.61%, jaws on-target — no drift",
    detail: null,
    basis: "residual within tolerance band",
  },
  hero: {
    id: "f_reject",
    label: "Reject rate",
    value: 4.61,
    unit: "%",
    window: "this month",
    window_start: "2026-06-01T00:00:00+02:00",
    window_end: "2026-06-13T14:00:00+02:00",
    denominator: { num: 479, den: 10386 },
    coverage: "ok",
    n: 10386,
    source_ref: "s_reject",
    freshness: null,
  },
  supporting: [
    {
      id: "f_resid",
      label: "Jaw residual (actual − setpoint) stddev",
      value: 0.78,
      unit: "mm",
      coverage: "ok",
      threshold: { target: 0.0, low: -1.5, high: 1.5 },
      n: 920,
      source_ref: "s_jaw",
      freshness: null,
    },
    {
      id: "f_failpick",
      label: "Failed picks (air-grab)",
      value: null,
      unit: null,
      coverage: "cold_armed",
      n: 0,
      source_ref: "s_failpick",
      freshness: null,
    },
  ],
  trend: {
    unit: "%",
    window: "this month",
    points: [
      { t: "2026-06-10", v: 4.4 },
      { t: "2026-06-11", v: 4.7 },
      { t: "2026-06-12", v: 4.6 },
    ],
    source_ref: "s_reject",
    downsample: { method: "mean", bucket_s: 86400, original_n: 10386 },
  },
  freshness: {
    state: "STALE",
    as_of: "2026-06-28T10:00:00+02:00",
    last_sample_at: "2026-06-18T03:31:41+02:00",
    age_seconds: 887899,
    source: "server_received_at",
    provenance: "live",
    event_time: null,
  },
  sources: [
    {
      id: "s_reject",
      signal_key: "reject",
      window: "this month",
      window_start: "2026-06-01T00:00:00+02:00",
      window_end: "2026-06-13T14:00:00+02:00",
      count: 479,
      source_grain: "rollup_cagg",
      read_source: "telemetry store",
      edge_path: null,
    },
    {
      id: "s_jaw",
      signal_key: "jaws_actual_mm-residual",
      window: "this month",
      window_start: "2026-06-01T00:00:00+02:00",
      window_end: "2026-06-13T14:00:00+02:00",
      count: 920,
      source_grain: "numeric",
      read_source: "telemetry store",
      edge_path: null,
    },
    {
      id: "s_failpick",
      signal_key: "failed_pick",
      window: "this month",
      window_start: "2026-06-01T00:00:00+02:00",
      window_end: "2026-06-13T14:00:00+02:00",
      count: 0,
      source_grain: "event",
      read_source: "telemetry store",
      edge_path: null,
    },
  ],
  links_to: { machine_card_tab: "arm" },
  live: null,
  frozen: { is_frozen: false, frozen_at: null },
};

/** The ARM oracle carries no machine GUID (machine_evidence has no identity
 *  block). This constant documents that the fixture is GUID-free so the SP-4
 *  no-GUID-in-DOM test can assert nothing leaks. */
export const ARM_EVIDENCE_HAS_NO_GUID = true;
